import {
  SparkActionSchema,
  SparkCreativeType,
  SparkFlow,
  SparkJob,
  SparkResultKind,
} from '../types/sparkTypes';

function formatFallbackName(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

function getCreativeTypeMap(creativeTypes: SparkCreativeType[] | undefined): Map<string, SparkCreativeType> {
  const map = new Map<string, SparkCreativeType>();
  (creativeTypes || []).forEach((creativeType) => {
    map.set(creativeType.fqn, creativeType);
  });
  return map;
}

function getCreativeTypeCopy(
  creativeTypes: SparkCreativeType[] | undefined,
  typeFqn: string | null | undefined,
  mode: 'singular' | 'plural'
): string {
  if (!typeFqn) {
    return mode === 'plural' ? 'items' : 'item';
  }

  const creativeType = getCreativeTypeMap(creativeTypes).get(typeFqn);
  if (!creativeType) {
    return mode === 'plural' ? 'items' : 'item';
  }

  if (mode === 'plural') {
    return (creativeType.plural_name || creativeType.name || 'items').toLowerCase();
  }

  return (creativeType.name || 'item').toLowerCase();
}

function getFlowAction(actionName: string | null | undefined, flow: SparkFlow | null): SparkActionSchema | null {
  if (!actionName || !flow?.actions?.length) {
    return null;
  }

  return flow.actions.find((action) => action.name.toLowerCase() === actionName.toLowerCase()) || null;
}

export function formatActionTemplate(
  template: string | undefined | null,
  sourceTypeFqn: string | null | undefined,
  targetTypeFqn: string | null | undefined,
  creativeTypes: SparkCreativeType[] | undefined
): string {
  let formatted = template || '';

  formatted = formatted.replace(/\{input_types\}/g, getCreativeTypeCopy(creativeTypes, sourceTypeFqn, 'plural'));
  formatted = formatted.replace(/\{input_type\}/g, getCreativeTypeCopy(creativeTypes, sourceTypeFqn, 'singular'));
  formatted = formatted.replace(/\{output_types\}/g, getCreativeTypeCopy(creativeTypes, targetTypeFqn, 'plural'));
  formatted = formatted.replace(/\{output_type\}/g, getCreativeTypeCopy(creativeTypes, targetTypeFqn, 'singular'));
  formatted = formatted.replace(/\{\w+\}/g, 'items');

  return formatted || 'Action';
}

export function getActionDisplayName(
  actionName: string | null | undefined,
  flow: SparkFlow | null,
  sourceTypeFqn?: string | null,
  targetTypeFqn?: string | null
): string {
  const action = getFlowAction(actionName, flow);
  if (!action) {
    return actionName ? formatFallbackName(actionName) : 'Action';
  }

  return formatActionTemplate(action.label, sourceTypeFqn, targetTypeFqn, flow?.creative_types);
}

export function getJobActionLabel(job: SparkJob, flow: SparkFlow | null): string {
  const actionName = job._action_args?.action || null;
  const action = getFlowAction(actionName, flow);
  if (!action) {
    return job.name || (actionName ? formatFallbackName(actionName) : 'Action');
  }

  return formatActionTemplate(action.label, job._source_type_fqn, job._target_type_fqn, flow?.creative_types);
}

function getResultLabel(resultKind: SparkResultKind | null | undefined, creativeTypeName: string): string {
  switch (resultKind) {
    case 'creation':
      return `Created new ${creativeTypeName}`;
    case 'iteration':
      return `New ${creativeTypeName} iteration ready`;
    case 'evaluation':
      return `Evaluation ready for ${creativeTypeName}`;
    case 'media':
      return 'Media ready';
    default:
      return 'Completed';
  }
}

export function getJobResultSummary(job: SparkJob, flow: SparkFlow | null): string {
  if (job._status === 'failed') {
    return 'Failed';
  }
  if (job._status === 'cancelled') {
    return 'Cancelled';
  }
  if (job._status !== 'completed') {
    return job._status === 'running' ? 'In progress' : 'Waiting to start';
  }

  const creativeTypeName =
    getCreativeTypeCopy(
      flow?.creative_types,
      job._result_kind === 'creation' ? job._target_type_fqn : job._source_type_fqn || job._target_type_fqn,
      'singular'
    ) || 'result';

  return getResultLabel(job._result_kind, creativeTypeName);
}
